import { DropdownMenu, DropdownMenuContent, DropdownMenuItem, DropdownMenuTrigger } from '@/ui/DropdownMenu';
import { DummyAvatar } from '@/ui/icons/DummyAvatar';
import { hashToNumber } from '@/utils';
import { getRemainder } from '@/utils/getRemainder';
import { DotsThreeVertical } from '@phosphor-icons/react/dist/ssr';
import classNames from 'classnames';
import { type PropsWithChildren, Suspense } from 'react';
import { PostCommentFormElement } from '../../postCommentFormElement';
import type { CommentData } from '../../types';
import { commentAuthorBorder } from '../PostCommentsFeed.css';
import { useComponentContext } from '../context/useComponentContext';
import { CommentDisplay } from './CommentDisplay';
import CommentLikeAction from './CommentLikeAction';
import CommentReplyAction from './CommentReplyAction';

type Props = PropsWithChildren<{
  comment: CommentData;
}>;

export function Comment({ comment, children }: Props) {
  const {
    canReply,
    showActions,
    isLive,
    replyNode,
    isSubmitting,
    onCommentChange,
    onClickReply,
    replyingToCommentId,
    onClickAction,
    avatarUrl,
    onClickLogin,
    onClickSubscribe,
    translations,
    isLoggedInAsAdmin,
    isVisitorLoggedIn,
    commenterId,
  } = useComponentContext();

  const handleClickReply = () => {
    onClickReply?.(replyingToCommentId ? null : comment.id);
  };

  const handleClickCancel = () => {
    onClickReply?.(null);
  };

  const isHighlighted = false; // TODO
  const isMaxDepthLevel = comment.depth_level >= comment.max_allowed_depth_level;

  const handleClickDelete = () => {
    onClickAction?.('delete', comment);
  };

  const handleClickReport = () => {
    onClickAction?.('report', comment);
  };

  const isModeratorComment = comment.is_moderator_comment;
  const showDeleteOption = comment.can_delete || isLoggedInAsAdmin;
  const subCommentsCount = comment.sub_comments.length > 0 ? comment.sub_comments.length : 0;

  const avatarIndex =
    typeof comment.commenter_id === 'string' ? hashToNumber(comment.commenter_id) : getRemainder(comment.commenter_id);

  return (
    <div className="flex flex-col gap-y-2">
      <div
        key={comment.id}
        className={classNames('flex w-full justify-between transition-all', isHighlighted && 'animate-pulse')}
        id={comment.id}
      >
        <div className="flex flex-row gap-x-3 w-full">
          <div className="flex flex-col h-full justify-start items-center">
            <div className={classNames('h-8 w-8 rounded-full object-cover grow-1 shrink-0', commentAuthorBorder)}>
              <DummyAvatar index={avatarIndex} className="w-full h-full rounded-full" />
            </div>
            <div className={classNames('w-px h-full grow-1', commentAuthorBorder)}>&nbsp;</div>
          </div>
          <div className="w-full flex flex-col gap-y-3">
            <div className="w-full flex flex-col gap-y-2">
              <CommentDisplay
                name={comment.commenter}
                content={comment.content}
                time={comment.created_at}
                isModerator={isModeratorComment}
              />

              <div className="flex items-center gap-x-4">
                <CommentLikeAction comment={comment} />
                {!isMaxDepthLevel && canReply && (
                  <CommentReplyAction onClick={handleClickReply} subCommentsCount={subCommentsCount} />
                )}
              </div>

              {canReply && replyingToCommentId === comment.id && replyNode && (
                <PostCommentFormElement
                  isSubmitting={isSubmitting}
                  isLive={isLive}
                  element={replyNode}
                  onCommentChange={onCommentChange}
                  onCancel={handleClickCancel}
                  isVisitorLoggedIn={isVisitorLoggedIn}
                  avatarUrl={avatarUrl}
                  onClickLogin={onClickLogin}
                  onClickSubscribe={onClickSubscribe}
                  translations={translations}
                  commenterId={commenterId}
                >
                  <input type="hidden" value={comment.id} name="comment_id" />
                </PostCommentFormElement>
              )}
            </div>
          </div>
        </div>

        {showActions && !isModeratorComment && (
          <Suspense>
            <DropdownMenu>
              <DropdownMenuTrigger className="hover:bg-gray-100">
                <DotsThreeVertical />
              </DropdownMenuTrigger>
              <DropdownMenuContent align="end">
                {showDeleteOption ? (
                  <DropdownMenuItem onClick={handleClickDelete} isDestructive>
                    Delete
                  </DropdownMenuItem>
                ) : (
                  <DropdownMenuItem onClick={handleClickReport} isDestructive>
                    Report
                  </DropdownMenuItem>
                )}
              </DropdownMenuContent>
            </DropdownMenu>
          </Suspense>
        )}
      </div>
      {children && (
        <div className="pl-8 w-full">
          {/* Sub-comments */}
          {children}
        </div>
      )}
    </div>
  );
}
